import type { ScreenPickerRequest, ScreenSource } from '@novachat/shared';
import { AppWindow, Monitor, TriangleAlert, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { desktop } from '../lib/desktop';
import { answerScreenPicker, listenForScreenPicker, useScreenPicker } from '../state/screen-picker';
import { useVoice } from '../state/voice';
import { closeOnBackdrop } from '../lib/dismiss';

/** Desktop app only: what to share, in our own design (browsers bring their own picker). Rendered once by Shell. */
export function ScreenPicker() {
  const request = useScreenPicker((s) => s.request);
  useEffect(() => listenForScreenPicker(), []);
  return request ? <PickerDialog key={request.id} request={request} /> : null;
}

function PickerDialog({ request }: { request: ScreenPickerRequest }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const screens = request.sources.filter((s) => s.kind === 'screen');
  const windows = request.sources.filter((s) => s.kind === 'window');
  const [tab, setTab] = useState<'screen' | 'window'>(screens.length > 0 || windows.length === 0 ? 'screen' : 'window');
  const [selected, setSelected] = useState<string | null>((screens[0] ?? windows[0])?.id ?? null);
  const [audio, setAudio] = useState(false);
  const inCall = useVoice((s) => s.room !== null);
  useEffect(() => {
    dialog.current?.showModal();
    // Start on the chosen picture rather than the close button.
    dialog.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus();
  }, []);
  // Leaving the call while choosing: nothing left to share into.
  useEffect(() => {
    if (!inCall) answerScreenPicker(null);
  }, [inCall]);

  const shown = tab === 'screen' ? screens : windows;
  // "Paylaş" always shares something on screen: a new tab starts on its first picture.
  const showTab = (next: 'screen' | 'window') => {
    setTab(next);
    setSelected(((next === 'screen' ? screens : windows)[0] ?? null)?.id ?? null);
  };
  const share = (sourceId: string | null) => sourceId && answerScreenPicker({ sourceId, audio });
  const tabClass = (active: boolean) =>
    `h-9 rounded-sm border-0 px-3 text-sm font-semibold ${active ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-2 hover:text-fg-1'}`;

  return (
    <dialog onClick={closeOnBackdrop}
      ref={dialog}
      aria-labelledby="picker-title"
      // A backdrop click closes the dialog: that gives up too.
      onClose={() => answerScreenPicker(null)}
      onCancel={(event) => {
        event.preventDefault();
        answerScreenPicker(null);
      }}
      className="m-auto w-[min(760px,calc(100vw-24px))] rounded-xl border border-line-strong bg-surface-1 p-0 text-fg-1 backdrop:bg-black/60"
    >
      <div className="flex items-center justify-between px-6 pt-5">
        <h2 id="picker-title" className="m-0 font-heading text-xl font-bold">
          Ne paylaşılsın?
        </h2>
        <button type="button" aria-label="Vazgeç" onClick={() => answerScreenPicker(null)} className="grid size-8 place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      <div role="tablist" className="mx-6 mt-3 flex gap-1 border-b border-line pb-2">
        <button type="button" role="tab" aria-selected={tab === 'screen'} onClick={() => showTab('screen')} className={tabClass(tab === 'screen')}>
          Ekranlar ({screens.length})
        </button>
        <button type="button" role="tab" aria-selected={tab === 'window'} onClick={() => showTab('window')} className={tabClass(tab === 'window')}>
          Pencereler ({windows.length})
        </button>
      </div>

      {request.permission === 'denied' && (
        <div className="mx-6 mt-4 flex flex-wrap items-center gap-3 rounded-md border border-line-strong bg-surface-2 p-3 text-[13.5px] text-fg-2">
          <span className="min-w-0 flex-[1_1_240px]">
            macOS ekran kaydına izin vermiyor. Sistem Ayarları › Gizlilik ve Güvenlik › Ekran ve Sistem Sesi Kaydı’ndan NovaChat’e izin ver, sonra NovaChat’i yeniden aç.
          </span>
          {desktop?.openScreenPermission && (
            <button type="button" onClick={() => desktop?.openScreenPermission?.()} className="nc-press h-9 rounded-sm border border-line-strong bg-surface-3 px-3 text-sm font-semibold">
              Ayarları aç
            </button>
          )}
        </div>
      )}

      <div
        role="group"
        aria-label={tab === 'screen' ? 'Ekranlar' : 'Pencereler'}
        className="mt-4 grid max-h-[50dvh] grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3 overflow-y-auto px-6 pb-1"
      >
        {shown.length === 0 && <p className="col-span-full m-0 py-6 text-center text-sm text-fg-3">Paylaşılabilecek {tab === 'screen' ? 'ekran' : 'pencere'} yok.</p>}
        {shown.map((source) => (
          <SourceCard key={source.id} source={source} selected={source.id === selected} onSelect={() => setSelected(source.id)} onShare={() => share(source.id)} />
        ))}
      </div>

      <div className="flex flex-col gap-4 px-6 pt-4 pb-5">
        {request.audio && (
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input type="checkbox" checked={audio} onChange={(e) => setAudio(e.target.checked)} className="mt-0.5 size-4 accent-brand-500" />
            <span>
              <span className="block font-semibold">Bilgisayarın sesini de paylaş</span>
              <span className="mt-0.5 flex items-start gap-1.5 text-[13px] text-fg-3">
                <TriangleAlert size={14} className="mt-0.5 shrink-0 text-yellow-500" aria-hidden="true" />
                Görüşmedekilerin sesi de yayına karışır, izleyenler konuşanları yankılı duyar. Video ya da müzik gösterirken aç.
              </span>
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => answerScreenPicker(null)} className="nc-press h-9 rounded-sm border border-line-strong bg-surface-2 px-3 text-sm font-semibold">
            Vazgeç
          </button>
          <button type="button" disabled={!selected} onClick={() => share(selected)} className="nc-press h-9 rounded-sm border-0 bg-brand-500 px-4 text-sm font-bold text-white disabled:opacity-50">
            Paylaş
          </button>
        </div>
      </div>
    </dialog>
  );
}

function SourceCard({ source, selected, onSelect, onShare }: { source: ScreenSource; selected: boolean; onSelect(): void; onShare(): void }) {
  const Fallback = source.kind === 'screen' ? Monitor : AppWindow;
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={source.name}
      onClick={onSelect}
      onDoubleClick={onShare}
      className={`nc-press flex min-w-0 flex-col gap-2 rounded-md border-2 p-2 text-left ${selected ? 'border-brand-500 bg-surface-3' : 'border-transparent bg-surface-2 hover:border-line-strong'}`}
    >
      <span className="grid aspect-video w-full place-items-center overflow-hidden rounded-sm bg-black/40">
        {source.thumbnail ? <img src={source.thumbnail} alt="" className="size-full object-contain" /> : <Fallback size={28} className="text-fg-3" aria-hidden="true" />}
      </span>
      <span className="flex min-w-0 items-center gap-2 text-[13px] font-semibold">
        {source.icon && <img src={source.icon} alt="" className="size-4 shrink-0" />}
        <span className="truncate">{source.name}</span>
      </span>
    </button>
  );
}
